/**
 * The expected plan (1.2).
 *
 * A referral tells a branch who the patient is, which disciplines are ordered
 * and what they were sent home with. It almost never says how many visits — the
 * amount is the branch's judgment. So every referral carries an **expected
 * plan**: a template the branch configured, suggested from the disciplines and
 * the case type, changeable by the person deciding, with a Custom escape when no
 * template fits. A referral that arrives with real frequencies (a hospital
 * discharge with orders on file) keeps those, and the dropdown reads
 * **Orders on file**.
 *
 * This is the one selector. The referral's tail in the estimate layer, the
 * panel's capacity row and what the hold reserves all read it, so changing the
 * plan moves all three and never moves them apart.
 */
import type { ConfigState, FrequencyOrder, Referral } from "@caredna-marlin/utils/homeHealthDemo/state";
import { describeFrequency, parseFrequency } from "@caredna-marlin/utils/homeHealthDemo/engine/frequency";

/** Weeks an expected plan reserves for when nobody has said otherwise. */
export const DEFAULT_PLAN_HORIZON_WEEKS = 4;

export const CUSTOM_PLAN_KEY = "__custom__";
export const ORDERS_ON_FILE_KEY = "__ordersOnFile__";

export const ORDERS_ON_FILE_LABEL = "Orders on file";
export const CUSTOM_LABEL = "Custom";

export interface PlanTemplate {
  /** Case-type key. Internal: it never reaches the screen. */
  key: string;
  name: string;
  orders: FrequencyOrder[];
}

export type PlanSource = "ordersOnFile" | "template" | "custom";

export interface ExpectedPlan {
  source: PlanSource;
  /** Set when `source` is "template". */
  templateKey?: string;
  /** What the dropdown shows and the queue's Expected plan column reads. */
  label: string;
  /** The value the dropdown is set to: a template key, or the Custom / Orders-on-file sentinels. */
  selectionKey: string;
  orders: FrequencyOrder[];
  horizonWeeks: number;
}

type PlanConfig = Pick<ConfigState, "starterFrequencies" | "planTemplateNames">;
type PlanReferral = Pick<Referral, "orders" | "caseType" | "expectedPlan">;

/** The branch's configured templates, by display name. The key never renders. */
export function planTemplates(config: PlanConfig): PlanTemplate[] {
  return Object.entries(config.starterFrequencies)
    .map(([key, orders]) => ({ key, name: config.planTemplateNames[key] ?? key, orders }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function planTemplate(config: PlanConfig, key: string | undefined): PlanTemplate | undefined {
  if (!key) return undefined;
  const orders = config.starterFrequencies[key];
  return orders ? { key, name: config.planTemplateNames[key] ?? key, orders } : undefined;
}

/** True when the referral arrived with real frequencies rather than eval-and-treat orders. */
export function hasFrequenciesOnFile(referral: Pick<Referral, "orders">): boolean {
  return referral.orders.length > 0 && referral.orders.every((o) => !parseFrequency(o.pattern).evalOnly);
}

/**
 * The template suggested for a referral: the one configured for its case type
 * if there is one, otherwise the template whose disciplines best match what was
 * ordered — most of the ordered disciplines covered, fewest extras, then by name
 * so the suggestion is stable.
 */
export function suggestTemplateKey(config: PlanConfig, referral: Pick<Referral, "orders" | "caseType">): string | undefined {
  if (config.starterFrequencies[referral.caseType]) return referral.caseType;
  const wanted = new Set(referral.orders.map((o) => o.discipline));
  if (!wanted.size) return undefined;
  const scored = planTemplates(config)
    .map((t) => {
      const has = new Set(t.orders.map((o) => o.discipline));
      const covered = [...wanted].filter((d) => has.has(d)).length;
      const extras = [...has].filter((d) => !wanted.has(d)).length;
      return { t, covered, extras };
    })
    .filter((s) => s.covered > 0)
    .sort((a, b) => b.covered - a.covered || a.extras - b.extras || a.t.name.localeCompare(b.t.name));
  return scored[0]?.t.key;
}

/** Keep the traits the referral asked for on the disciplines a template covers. */
function withAskedTraits(orders: FrequencyOrder[], referral: Pick<Referral, "orders">): FrequencyOrder[] {
  return orders.map((o) => {
    const asked = referral.orders.find((r) => r.discipline === o.discipline);
    const traits = Array.from(new Set([...(o.requiredTraits ?? []), ...(asked?.requiredTraits ?? [])]));
    return traits.length ? { ...o, requiredTraits: traits } : o;
  });
}

/**
 * The expected plan for a referral: the choice if the branch made one, else the
 * orders when they carry frequencies, else the suggested template.
 */
export function expectedPlanFor(config: PlanConfig, referral: PlanReferral): ExpectedPlan {
  const ordersOnFile: ExpectedPlan = {
    source: "ordersOnFile",
    label: ORDERS_ON_FILE_LABEL,
    selectionKey: ORDERS_ON_FILE_KEY,
    orders: referral.orders,
    horizonWeeks: DEFAULT_PLAN_HORIZON_WEEKS,
  };
  const fromTemplate = (key: string, horizonWeeks = DEFAULT_PLAN_HORIZON_WEEKS): ExpectedPlan | undefined => {
    const template = planTemplate(config, key);
    if (!template) return undefined;
    return { source: "template", templateKey: key, label: template.name, selectionKey: key, orders: withAskedTraits(template.orders, referral), horizonWeeks };
  };

  const choice = referral.expectedPlan;
  if (choice?.kind === "custom") {
    return { source: "custom", label: CUSTOM_LABEL, selectionKey: CUSTOM_PLAN_KEY, orders: withAskedTraits(choice.orders, referral), horizonWeeks: choice.horizonWeeks };
  }
  if (choice?.kind === "template") {
    const chosen = fromTemplate(choice.templateKey);
    if (chosen) return chosen;
  }
  if (choice?.kind === "ordersOnFile" && hasFrequenciesOnFile(referral)) return ordersOnFile;

  if (hasFrequenciesOnFile(referral)) return ordersOnFile;
  const suggested = suggestTemplateKey(config, referral);
  return (suggested ? fromTemplate(suggested) : undefined) ?? ordersOnFile;
}

/**
 * The plan in words for one discipline row: "SOC, 2 a week × 3 wks, then every
 * other week × 6 wks". The start of care is named because the template's weeks
 * run from it.
 */
export function describePlanPattern(pattern: string): string {
  const parsed = parseFrequency(pattern);
  if (parsed.evalOnly) return "evaluation, frequencies to follow";
  return `SOC, ${describeFrequency(parsed)}`;
}

/** The disciplines an expected plan covers, in the plan's own order. */
export function planDisciplines(plan: Pick<ExpectedPlan, "orders">): FrequencyOrder["discipline"][] {
  return Array.from(new Set(plan.orders.map((o) => o.discipline)));
}
